import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from "react";
import { isValidElement } from "react";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
import { CodeBlock } from "./code-block";
import { cn } from "./lib/cn";
import type { ToolUIPart } from "./types";
import { Badge } from "./ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";

export type ToolProps = ComponentProps<typeof Collapsible>;
/** Collapsible shell for one AI tool invocation. */
export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible className={cn("not-prose mb-4 w-full rounded-md border", className)} {...props} />
);
const statusLabels = {
  "input-streaming": "Pending",
  "input-available": "Running",
  "approval-requested": "Awaiting Approval",
  "approval-responded": "Responded",
  "output-available": "Completed",
  "output-error": "Error",
  "output-denied": "Denied",
} satisfies Record<ToolUIPart["state"], string>;
const statusIcons = {
  "input-streaming": <CircleIcon className="size-4" />,
  "input-available": <ClockIcon className="size-4 animate-pulse" />,
  "approval-requested": <ClockIcon className="size-4 text-yellow-600" />,
  "approval-responded": <CheckCircleIcon className="size-4 text-blue-600" />,
  "output-available": <CheckCircleIcon className="size-4 text-green-600" />,
  "output-error": <XCircleIcon className="size-4 text-red-600" />,
  "output-denied": <XCircleIcon className="size-4 text-orange-600" />,
} satisfies Record<ToolUIPart["state"], ReactNode>;
export type ToolHeaderProps = {
  title?: string;
  type: ToolUIPart["type"];
  state: ToolUIPart["state"];
  className?: string;
  /** Set false when the tool row has no content to reveal; hides the expand affordance. */
  expandable?: boolean;
  /** Localized label for the current state. */
  statusLabel?: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type">;
/** Shows the current state of an AI tool call and expands when content is available. */
export const ToolHeader = ({
  className,
  title,
  type,
  state,
  statusLabel,
  expandable = true,
  ...props
}: ToolHeaderProps) => (
  <CollapsibleTrigger
    className={cn("flex w-full items-center justify-between gap-4 p-3", className)}
    disabled={!expandable || undefined}
    {...props}
  >
    <div className="flex items-center gap-2">
      <WrenchIcon className="size-4 text-muted-foreground" />
      <span className="font-medium text-sm">{title ?? type}</span>
      <Badge className="gap-1.5 rounded-full text-xs" variant="secondary">
        {statusIcons[state]}
        {statusLabel ?? statusLabels[state]}
      </Badge>
    </div>
    {expandable && (
      <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
    )}
  </CollapsibleTrigger>
);
export const ToolContent = ({ className, ...props }: ComponentProps<typeof CollapsibleContent>) => (
  <CollapsibleContent
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
      className,
    )}
    {...props}
  />
);
/** Displays structured tool-call input. */
export const ToolInput = ({
  className,
  input,
  ...props
}: ComponentProps<"div"> & { input: ToolUIPart["input"] }) => (
  <div className={cn("space-y-2 overflow-hidden p-4", className)} {...props}>
    <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
      Parameters
    </h4>
    <div className="rounded-md bg-muted/50">
      <CodeBlock code={JSON.stringify(input, null, 2)} language="json" />
    </div>
  </div>
);

const isStringToolOutput = (output: ToolUIPart["output"]): output is string =>
  Object.prototype.toString.call(output) === "[object String]";

const isObjectToolOutput = (output: ToolUIPart["output"]): output is object =>
  output !== null && Object(output) === output;

const getToolOutputContent = (output: ToolUIPart["output"]): ReactNode => {
  if (output === null || output === undefined) {
    return null;
  }
  if (isValidElement(output)) {
    return output;
  }
  if (isStringToolOutput(output)) {
    return <CodeBlock code={output} language="json" />;
  }
  if (isObjectToolOutput(output)) {
    const formattedOutput = JSON.stringify(output, null, 2);
    return formattedOutput === undefined ? null : (
      <CodeBlock code={formattedOutput} language="json" />
    );
  }
  return <div>{String(output)}</div>;
};

/** Displays structured tool-call output or an error. */
export const ToolOutput = ({
  className,
  output,
  errorText,
  ...props
}: ComponentProps<"div"> & {
  output: ToolUIPart["output"];
  errorText: ToolUIPart["errorText"];
}) => {
  if (output === null || output === undefined) {
    if (!errorText) {
      return null;
    }
  }
  if (!output && !errorText) {
    return null;
  }
  const content = getToolOutputContent(output);
  return (
    <div className={cn("space-y-2 p-4", className)} {...props}>
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {errorText ? "Error" : "Result"}
      </h4>
      <div
        className={cn(
          "overflow-x-auto rounded-md text-xs [&_table]:w-full",
          errorText ? "bg-destructive/10 text-destructive" : "bg-muted/50 text-foreground",
        )}
      >
        {errorText && <div>{errorText}</div>}
        {content}
      </div>
    </div>
  );
};
